import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { API } from '@constants/api.constants';
import { TOASTER_TYPE } from '@constants/app.enums';
import { STORAGE } from '@constants/storage.constant';
import {
  AuthPayload,
  ChangePasswordPayload,
  LoginOtpResponse,
  MemberDetail,
  ResetPasswordPayload,
  SignUpPayload,
  SurePassComplete,
  VerifiedUserResponse
} from '@models/auth.model';
import {
  APIResponseModel,
  PendingRequestsResponse
} from '@models/common.model';
import { SessionInitService } from '@services/session-init.service';
import { SessionTimeoutService } from '@services/session-timeout.service';
import { StorageService } from '@services/storage.service';
import { ToasterService } from '@services/toaster.service';
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import { PricingService } from './pricing.service';
import { ProfileService } from './profile.service';
import { SocketService } from './socket.service';
import { UserConfigService } from './user-config.service';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);
  private readonly httpBackend = inject(HttpBackend);
  private readonly sessionInitService = inject(SessionInitService);
  private readonly sessionTimeoutService = inject(SessionTimeoutService);
  private readonly userStateService = inject(UserStateService);
  private readonly socketService = inject(SocketService);
  private readonly pricingService = inject(PricingService);
  private readonly userConfigService = inject(UserConfigService);
  private readonly profileService = inject(ProfileService);
  private readonly toasterService = inject(ToasterService);
  private readonly verificationStateSubject = new BehaviorSubject<boolean>(
    false
  );

  pendingRequests = signal<PendingRequestsResponse | null>(null);
  isEmailVerified = signal(false);
  emailVerificationMessage = signal('');
  verificationState$ = this.verificationStateSubject.asObservable();

  private readonly noInterceptorHttp: HttpClient;

  constructor() {
    const httpBackend = this.httpBackend;
    this.noInterceptorHttp = new HttpClient(httpBackend);
  }

  setVerificationState(verified: boolean, message = '') {
    this.isEmailVerified.set(verified);
    this.emailVerificationMessage.set(message);
    this.verificationStateSubject.next(verified);
  }

  login(params: AuthPayload) {
    return this.http
      .post<APIResponseModel<MemberDetail>>(API.LOGIN, params, {
        withCredentials: true // receive the httpOnly refresh_token cookie
      })
      .pipe(tap((member) => this.persistLoginData(member.data)));
  }

  /**
   * Silently mint a new access token from the httpOnly refresh_token cookie.
   * Uses noInterceptorHttp so this call never recurses through
   * HttpErrorInterceptor's own 401-refresh handling.
   */
  refreshAccessToken() {
    return this.noInterceptorHttp
      .post<
        APIResponseModel<{ access_token: string; session_id?: string }>
      >(API.REFRESH_API, {}, { withCredentials: true })
      .pipe(
        tap((res) => {
          this.userStateService.setToken(res.data.access_token);
          if (res.data.session_id) {
            this.userStateService.setSessionId(res.data.session_id);
          }
        })
      );
  }

  /**
   * Called once at app bootstrap (see app.config.ts provideAppInitializer).
   * Access token now lives in memory only, so a hard page reload always starts
   * empty — this silently re-derives a fresh access token from the httpOnly
   * refresh_token cookie (if any) and rehydrates the user profile, so a
   * previously logged-in user doesn't get bounced to /login on every reload.
   * Never throws: no cookie / expired session just means "start logged out."
   */
  tryRestoreSession() {
    // Anonymous visitors landing directly on an auth-section page (login,
    // logout, forgot-password, sign-up, reset-password) can never have a
    // session to restore — skip the guaranteed-401 refresh+profile round trip
    // so first paint on the login screen isn't delayed by it. Checked as a
    // substring (not a leading-slash prefix) so it matches under any baseHref,
    // e.g. both '/auth/login' and the production issuer-path build's
    // '/issuer/auth/login'.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.includes('/auth/')
    ) {
      return of(false);
    }

    return this.refreshAccessToken().pipe(
      switchMap(() => {
        // noInterceptorHttp, not this.http: we just minted a token above, so
        // this call must not be able to trigger a SECOND, uncoordinated
        // HttpErrorInterceptor refresh attempt on a 401 — two refresh calls
        // racing to rotate the same single-use refresh_token cookie is
        // exactly what leaves the session broken on the next reload. If this
        // call fails for any reason, the catchError below already treats
        // that as "no session" — there is nothing an interceptor-level retry
        // would add here.
        const token = this.userStateService.getToken();
        const headers = token
          ? new HttpHeaders({ Authorization: `Bearer ${token}` })
          : undefined;
        return this.noInterceptorHttp.get<APIResponseModel<MemberDetail>>(
          API.PROFILE,
          { headers }
        );
      }),
      tap((res) => this.userStateService.setUserData(res.data)),
      map(() => true),
      catchError(() => {
        this.userStateService.clearUser();
        return of(false);
      })
    );
  }

  sendOtp(params: AuthPayload) {
    const httpParams = new HttpParams().set('function', 'LOGIN');
    return this.http.post<LoginOtpResponse>(API.SEND_OTP, params, {
      params: httpParams
    });
  }

  verifyOtp(params: { otp_code: string; reference_id: string }) {
    const httpParams = new HttpParams().set('function', 'LOGIN');
    return this.http
      .post<
        APIResponseModel<MemberDetail>
      >(API.VERIFY_OTP, params, { params: httpParams, withCredentials: true })
      .pipe(tap((member) => this.persistLoginData(member.data)));
  }

  verifyOtpForChangePassword(params: {
    otp_code: string;
    reference_id: string;
  }) {
    const httpParams = new HttpParams().set('function', 'change_password');
    return this.http.post<APIResponseModel<{ verified: boolean }>>(
      API.VERIFY_OTP,
      params,
      { params: httpParams }
    );
  }

  sendOtpForChangePassword(mobileNumber: string) {
    const httpParams = new HttpParams().set('function', 'change_password');

    return this.http.post<LoginOtpResponse>(
      API.SEND_OTP,
      { mobile_number: mobileNumber },
      { params: httpParams }
    );
  }

  completeSurePass(params: { client_id: string }) {
    const tempToken = this.storageService.getFromLocal(STORAGE.TEMPORARY_TOKEN);

    let headers = new HttpHeaders();
    if (tempToken) {
      headers = headers.set('Authorization', `Bearer ${tempToken}`);
    }

    headers = headers.set(
      'X-Timezone-Offset',
      String(new Date().getTimezoneOffset())
    );

    const url = `${API.SURE_PASS_COMPLETE_FLOW}/${params.client_id}`;

    return this.noInterceptorHttp.get<SurePassComplete>(url, { headers }).pipe(
      tap(() => {
        this.sessionInitService.initializeSessionIfLoggedIn();
      })
    );
  }

  logout() {
    return this.http
      .post<APIResponseModel<null>>(
        API.LOGOUT,
        {},
        { withCredentials: true } // so the backend can read+revoke the refresh_token cookie
      )
      .pipe(finalize(() => this.clearLogoutState()));
  }

  forgotPassword(params: Pick<AuthPayload, 'email'>) {
    return this.http.post<APIResponseModel<null>>(API.FORGOT_PASSWORD, params);
  }

  setPassword(payload: ResetPasswordPayload, token: string) {
    const form = new FormData();
    form.append('confirm_password', payload.confirm_password);
    form.append('new_password', payload.new_password);

    if (payload.recaptcha_token) {
      form.append('recaptcha_token', payload.recaptcha_token);
    }

    return this.http.post<APIResponseModel<null>>(
      `${API.RESET_PASSWORD}/${token}`,
      form
    );
  }

  changePassword(payload: ChangePasswordPayload) {
    return this.http
      .post<
        APIResponseModel<{ token: string }> | LoginOtpResponse
      >(API.CHANGE_PASSWORD, payload)
      .pipe(
        tap((res) => {
          if ('data' in res && res.data && 'token' in res.data) {
            this.storageService.set(
              STORAGE.LOGIN_TOKEN,
              (res as APIResponseModel<{ token: string }>).data.token
            );
          }
        })
      );
  }

  fetchPendingRequests() {
    this.http
      .get<APIResponseModel<PendingRequestsResponse>>(`${API.PENDING_REQUESTS}`)
      .subscribe({
        next: (response) => {
          this.pendingRequests.set(response.data);
        },
        error: () => {
          this.pendingRequests.set(null);
        }
      });
  }

  signUp(params: Partial<SignUpPayload>) {
    return this.http.post<APIResponseModel<null>>(API.SIGNUP, params);
  }

  emailConfirmation(email: string) {
    return this.http.post<APIResponseModel<null>>(API.EMAIL_CONFIRMATION, {
      email
    });
  }

  /** Persists auth data from a successful login/OTP-verify response. */
  private persistLoginData(data: MemberDetail): void {
    this.storageService.removeFromLocal(STORAGE.ACTIVE_WORKSPACE);
    this.storageService.removeFromLocal(STORAGE.WORKSPACE);
    // Auth token, session id and profile PII (name/email/uuid/image) are kept
    // in memory only (UserStateService) — never localStorage — so they can't
    // be exfiltrated via XSS the way a persisted value could (AS-08/AS-09).
    // The backend no longer returns refresh_token in this response body at
    // all: it is delivered exclusively via the httpOnly refresh_token cookie.
    this.storageService.setToLocal(STORAGE.USER_ROLES, data.roles || []);

    this.userStateService.setToken(data.token);
    if (data.session_id) {
      this.userStateService.setSessionId(data.session_id);
    }
    this.userStateService.updateUserData(data);

    if (data.suspicious) {
      const country = data.suspicious_country ?? 'an unrecognised location';
      this.toasterService.display(
        `New sign-in detected from ${country}. If this wasn't you, go to Active Sessions and log out all devices.`,
        TOASTER_TYPE.WARNING
      );
    }

    if (data.session_limit_exceeded) {
      this.toasterService.display(
        'An older session on another device was ended to enforce your session limit.',
        TOASTER_TYPE.INFO
      );
    }

    this.sessionInitService.initializeSessionIfLoggedIn();
  }

  private clearLogoutState(): void {
    this.profileService.reset();
    this.socketService.disconnect();
    this.sessionInitService.cleanupSession();
    // Close any open dialog (e.g. a consent modal) and reset consent state so the
    // next login re-prompts from a clean state (root singletons survive SPA logout).
    this.sessionTimeoutService.tearDownDialogsAndConsent();
    // FS-07: wipe ALL client state via a single deny-list clear() instead of an
    // enumerated allow-list. clear() drops localStorage + sessionStorage + the
    // in-memory API response cache (which can hold PII), preserving only the
    // language key — so no per-key removal (or per-caller cleanup) can be
    // forgotten as new storage keys are added. This is the single source of
    // truth for logout teardown; every caller (header, change-password, etc.)
    // inherits complete clearing without repeating it.
    this.storageService.clear();
    this.userStateService.clearUser();
    this.userConfigService.clearConfig();
    this.pricingService.refreshPlanData();
  }

  emailVerification(params: { verify_token: string }) {
    const httpParams = this.createHttpParamsFromPartial(params);
    return this.noInterceptorHttp.post<APIResponseModel<VerifiedUserResponse>>(
      API.EMAIL_VERIFICATION,
      {},
      {
        params: httpParams
      }
    );
  }

  createHttpParamsFromPartial(partial: unknown): HttpParams {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let httpParams = new HttpParams();

    const traverseParams = (params: Record<string, any>) => {
      for (const key in params) {
        if (typeof params[key] === 'object') {
          return traverseParams(params[key]);
        }
        if (Object.hasOwn(params, key)) {
          httpParams = httpParams.set(key, String(params[key]));
        }
      }
    };

    traverseParams(partial as Record<string, any>);
    return httpParams;
    /* eslint-enable @typescript-eslint/no-explicit-any */
  }
}
